// Iter78 — Approved Partner Stores
// =====================================
// Public-shop section that lists every approved partner store as a square
// logo card. Clicking the card opens that partner's storefront where their
// approved products are shown. Uses the existing `/api/storefront/partners`
// endpoint (Iter77-compatible) and the existing `/shop/partner/:slug`
// route (Iter76-compatible). NO upstream business logic is changed by
// this file — it only renders.
//
// Layout rules locked by user:
//   • Square cards. Logo must preserve aspect ratio (object-contain, not
//     object-cover) and never stretch/distort.
//   • Fallback to LATM placeholder when a partner has not uploaded a logo
//     (handled by SafeImage).
//   • Show the first 10 partners. If more exist, show a "View all
//     partners →" link.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Package, Building2, ArrowRight } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import SafeImage from "@/components/SafeImage";

const TYPE_ICON = { store: Store, brand: Package, company: Building2 };
const TYPE_LABEL = { store: "Partner Store", brand: "Partner Brand", company: "Partner Company" };
const FIRST_PAGE_LIMIT = 10;

export default function PartnerShopSection({ partnerType = null, limit = FIRST_PAGE_LIMIT }) {
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = partnerType ? `?partner_type=${partnerType}` : "";
        const { data } = await api.get(`/storefront/partners${params}`);
        if (cancelled) return;
        const list = Array.isArray(data?.partners) ? data.partners : [];
        // Iter78 — Only filter on business_name (so a partner without a
        // logo is still reachable; SafeImage falls back to the LATM
        // placeholder).
        const usable = list.filter(
          (p) => p.business_name && p.business_name.trim(),
        );
        setTotal(usable.length);
        setPartners(usable.slice(0, limit));
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerType, limit]);

  if (loading) {
    return (
      <section className="my-12" data-testid="partner-shop-section-loading">
        <div className="text-xs uppercase tracking-[0.2em] text-lifted-dim font-bebas">
          Loading approved partner stores…
        </div>
      </section>
    );
  }
  if (err)              return null;   // Fail silently on the public page.
  if (partners.length === 0) return null;

  const hasMore = total > limit;

  return (
    <section className="my-12" data-testid="partner-shop-section">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow text-lifted-green">Lifted Partners</div>
          <h2 className="font-anton text-3xl uppercase text-white"
              data-testid="partner-shop-heading">
            Approved Partner Stores
          </h2>
          <p className="text-lifted-dim text-sm mt-1 max-w-2xl">
            Independent shops, brands, and companies plugged into Lifted.
            Tap any logo to open that partner's storefront.
          </p>
        </div>
        {hasMore && (
          <Link
            to="/partners"
            data-testid="partner-shop-view-all"
            className="inline-flex items-center gap-1 text-sm font-bebas tracking-[0.18em] uppercase text-lifted-green hover:text-white transition"
          >
            View all partners <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </header>
      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
        data-testid="partner-shop-grid"
      >
        {partners.map((p) => {
          const Icon = TYPE_ICON[p.partner_type] || Store;
          return (
            <Link
              key={p.id}
              to={`/shop/partner/${p.slug}`}
              data-testid={`partner-card-${p.slug}`}
              className="group glass-panel p-3 hover:border-lifted-green/50 transition flex flex-col items-center text-center"
            >
              {/* Square card · `object-contain` keeps the logo proportional
                  (never stretched / cropped). Padding keeps a clean margin
                  so even bright logos breathe on the dark surface. */}
              <div className="w-full aspect-square rounded-md bg-white/5 grid place-items-center overflow-hidden mb-2 p-2">
                <SafeImage
                  src={p.logo_url}
                  alt={p.business_name || "Partner logo"}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </div>
              <div className="text-xs font-anton uppercase text-white truncate w-full"
                   data-testid={`partner-card-name-${p.slug}`}>
                {p.business_name || "Lifted Partner"}
              </div>
              <div className="text-[10px] text-lifted-dim font-mono">
                {TYPE_LABEL[p.partner_type] || "Partner"}
                {p.city ? ` · ${p.city}` : ""}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
